<template>
  <SlotPanel classes="!p-0">
    <div class="px-4 pt-4" id="performance-addons-block">
      <LabelReset
        id="performance-addons-label"
        class="mb-2"
        :modelValue="updatedPerformance.add_ons"
        :initialValue="initialAddOns"
        @update:modelValue="performanceUpdateStore.resetAllAddOns"
      >
        Add-Ons in ticket purchase
      </LabelReset>
      <div v-if="addOnsFromPerformance.length > 0" class="flex flex-col gap-3">
        <PerformanceAddOnPodItem
          v-for="addOn in addOnsFromPerformance"
          :key="addOn.id"
          :addOn="addOn"
          @update:addOn="performanceUpdateStore.updateAddOn"
          :initialAddOn="_.find(selectedPerformance.add_ons, { id: addOn.id })"
          :status="addOn.status"
          class="cursor-pointer"
          @click="goToAddOnEditPage(addOn)"
        />
      </div>
      <p v-else id="no-performance-addons-text" class="text-fg-secondary">
        No add-ons have been added to this performance yet.
      </p>
    </div>
    <hr class="border-linear-subtle my-4" v-show="showAvailableAddOns" />
    <transition
      enter-from-class="h-0 opacity-0"
      enter-active-class="ease-in-out duration-500"
      enter-to-class="h-full opacity-100"
      leave-from-class="h-full opacity-100"
      leave-active-class="ease-in-out duration-200"
      leave-to-class="h-0 opacity-0"
    >
      <div v-show="showAvailableAddOns" id="available-addons-block" class="px-4 pb-4">
        <LabelDefault id="available-addons-label" class="mb-2 inline-block">
          Available Add-Ons
        </LabelDefault>
        <div v-if="availableAddOns.length > 0" class="flex flex-col gap-3">
          <PerformanceAddOnPodItem
            v-for="addOn in availableAddOns"
            :key="addOn.id"
            :addOn="addOn"
            :status="UPDATE_INDICATOR_STATUS.NONE"
            class="cursor-pointer"
            @click="performanceUpdateStore.addAddOn(addOn)"
            isAvailable
          />
        </div>
        <p v-else id="no-available-addons-text" class="text-fg-secondary">
          All add-ons have already been added to this performance.
        </p>
      </div>
    </transition>

    <div
      id="toggle-available-addons"
      @click="showAvailableAddOns = !showAvailableAddOns"
      class="w-10 h-10 border-s border-t border-linear-subtle flex items-center justify-center cursor-pointer hover:bg-surface-secondary ml-auto"
      :class="showAvailableAddOns ? 'mt-0' : 'mt-4'"
    >
      <component
        class="text-fg-secondary w-4 h-4"
        :is="showAvailableAddOns ? ChevronUpIcon : ChevronDownIcon"
      />
    </div>
  </SlotPanel>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onBeforeMount, ref } from 'vue'
import { useRouter } from 'vue-router'

import ChevronDownIcon from '@/assets/chevronDown.svg?component'
import ChevronUpIcon from '@/assets/chevronUp.svg?component'
import LabelDefault from '@/common/components/atoms/LabelDefault.vue'
import LabelReset from '@/common/components/molecules/LabelReset.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'
import PerformanceAddOnPodItem from '@/modules/performance/components/molecules/PerformanceAddOnPodItem.vue'
import { PERFORMANCE_ROUTE_NAMES } from '@/modules/performance/constants/PerformanceRouteConstants'
import {
  UPDATE_INDICATOR_STATUS,
  UpdateIndicatorStatus,
} from '@/modules/performance/interfaces/PerformanceTableInterfaces'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import { usePerformanceUpdateStore } from '@/modules/performance/stores/PerformanceUpdateStore'
import { useAddOnStore } from '@/stores/addons'

import { AddOnRead, PerformanceAddOnRead } from '@generated/types'

type PerformanceAddWithStatus = PerformanceAddOnRead & { status: UpdateIndicatorStatus }

const addOnStore = useAddOnStore()
const { addOns } = storeToRefs(addOnStore)
const router = useRouter()
onBeforeMount(async () => {
  await addOnStore.fetchAll()
  if (addOnStore.addOns.length === 0) {
    return router.replace({ name: PERFORMANCE_ROUTE_NAMES.SCHEDULE })
  }
})

const { selectedPerformance } = storeToRefs(usePerformanceReservationStore())
const initialAddOns = computed<PerformanceAddOnRead[]>(function () {
  if (!selectedPerformance.value) return []
  return selectedPerformance.value.add_ons
})

const performanceUpdateStore = usePerformanceUpdateStore()
const { updatedPerformance } = storeToRefs(performanceUpdateStore)
const addOnsFromPerformance = computed<PerformanceAddWithStatus[]>(function () {
  return updatedPerformance.value!.add_ons.map(addOn => {
    const initialAddOn = _.find(selectedPerformance.value!.add_ons, { id: addOn.id })
    if (!initialAddOn) return { ...addOn, status: UPDATE_INDICATOR_STATUS.NEW, is_active: true }
    return {
      ...addOn,
      status: _.isEqual(addOn, initialAddOn)
        ? UPDATE_INDICATOR_STATUS.NONE
        : UPDATE_INDICATOR_STATUS.UPDATED,
    }
  })
})
const usedAddOnIds = computed<number[]>(function () {
  if (!addOnsFromPerformance.value) return []
  return addOnsFromPerformance.value.map(addOn => addOn.parent_id ?? addOn.id)
})
const showAvailableAddOns = ref<boolean>(false)
const availableAddOns = computed<AddOnRead[]>(function () {
  return addOns.value.filter(addOn => !usedAddOnIds.value.includes(addOn.id))
})

async function goToAddOnEditPage(addOn: PerformanceAddWithStatus) {
  if (addOn.status === UPDATE_INDICATOR_STATUS.NEW) {
    performanceUpdateStore.removeAddOnFromPerformance(addOn.id)
  } else {
    await router.replace({
      name: PERFORMANCE_ROUTE_NAMES.ADD_ON_EDIT,
      params: { addOnId: addOn.id.toString() },
    })
  }
}
</script>
